import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { EmptyState } from '@/components/shared/EmptyState'
import { StatusBadge } from '@/components/shared/StatusBadge'
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Mail,
  XCircle,
  ChevronRight,
  Shield,
  RefreshCw,
} from 'lucide-react'

interface DomainDeliverability {
  domain_id: string
  domain_name: string
  domain_status: string
  sent: number
  delivered: number
  bounced: number
  spam: number
  reputation_score: number
  reputation_state: 'measured' | 'insufficient_data'
  reputation_sample_size: number
}

interface DeliverabilityData {
  overall: {
    sent: number
    delivered: number
    bounced: number
    spam: number
  }
  overall_score: number | null
  overall_reputation_state: 'measured' | 'insufficient_data'
  overall_reputation_sample_size: number
  domains: DomainDeliverability[]
}

export function Deliverability() {
  const navigate = useNavigate()
  const [data, setData] = useState<DeliverabilityData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchDeliverability = async () => {
    try {
      const res = await api.get('/deliverability')
      setData(res.data)
    } catch {
      toast.error('Failed to load deliverability data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchDeliverability()
  }, [])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchDeliverability()
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-accent rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-accent rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  const overall = data?.overall
  const hasRecentSendingData = Boolean(overall?.sent)
  const deliveryRate = hasRecentSendingData ? Math.round((overall!.delivered / overall!.sent) * 100) : 0
  const bounceRate = hasRecentSendingData ? Math.round((overall!.bounced / overall!.sent) * 100) : 0
  const spamRate = hasRecentSendingData ? Math.round((overall!.spam / overall!.sent) * 100) : 0

  const alerts: string[] = []
  if (bounceRate > 5) {
    alerts.push(`High bounce rate detected (${bounceRate}%). Review your recipient lists and domain configuration.`)
  }
  if (spamRate > 1) {
    alerts.push(`Spam complaint rate is elevated (${spamRate}%). Consider reviewing email content and engagement practices.`)
  }
  if (overall && overall.sent > 0 && deliveryRate < 95) {
    alerts.push(`Delivery rate is below 95% (${deliveryRate}%). Check domain reputation and DNS records.`)
  }
  data?.domains.forEach((d) => {
    if (d.reputation_state === 'measured' && d.reputation_score < 70) {
      alerts.push(`Low reputation score for ${d.domain_name} (${d.reputation_score}/100). Review sending practices.`)
    }
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Deliverability</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Delivery and domain reputation
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data?.overall_reputation_state === 'measured' && data.overall_score !== null ? (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Overall Score</p>
              <p
                className={`text-sm font-semibold ${
                  data.overall_score >= 80
                    ? 'text-emerald-400'
                    : data.overall_score >= 60
                      ? 'text-amber-400'
                      : 'text-red-400'
                }`}
              >
                {data.overall_score}/100
              </p>
            </div>
          ) : (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Overall Score</p>
              <p className="text-sm font-semibold text-muted-foreground">Not enough data</p>
            </div>
          )}
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing} className="shrink-0">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
            >
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-300">{alert}</p>
            </div>
          ))}
        </div>
      )}

      {/* Overall Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Mail className="h-4 w-4 text-blue-400" />}
          label="Total Sent (7d)"
          value={overall?.sent ?? 0}
        />
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          label="Delivered (7d)"
          value={overall?.delivered ?? 0}
          subValue={hasRecentSendingData ? `${deliveryRate}% rate` : 'No recent data'}
          positive
        />
        <StatCard
          icon={<XCircle className="h-4 w-4 text-red-400" />}
          label="Bounced (7d)"
          value={overall?.bounced ?? 0}
          subValue={hasRecentSendingData ? `${bounceRate}% rate` : 'No recent data'}
          negative={bounceRate > 5}
        />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4 text-amber-400" />}
          label="Spam (7d)"
          value={overall?.spam ?? 0}
          subValue={hasRecentSendingData ? `${spamRate}% rate` : 'No recent data'}
          negative={spamRate > 1}
        />
      </div>

      {/* Domain List */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Per-Domain Deliverability (7d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.domains && data.domains.length > 0 ? (
            <div className="space-y-2">
              {data.domains.map((domain) => {
                const dRate = domain.sent
                  ? Math.round((domain.delivered / domain.sent) * 100)
                  : 0
                const bRate = domain.sent
                  ? Math.round((domain.bounced / domain.sent) * 100)
                  : 0
                const sRate = domain.sent
                  ? Math.round((domain.spam / domain.sent) * 100)
                  : 0
                const reputationStatus =
                  domain.domain_status === 'active'
                    ? 'active'
                    : domain.domain_status === 'suspended'
                      ? 'suspended'
                      : 'pending'

                const reputationLabel =
                  domain.reputation_state === 'insufficient_data'
                    ? 'Not enough data'
                    : `${domain.reputation_score}/100`

                return (
                  <div
                    key={domain.domain_id}
                    role="button"
                    tabIndex={0}
                    aria-label={`View deliverability details for ${domain.domain_name}`}
                    className="flex items-center justify-between py-3 border-b border-border/50 last:border-0 cursor-pointer hover:bg-accent/30 rounded-md px-2 transition-colors"
                    onClick={() => navigate(`/deliverability/domains/${domain.domain_id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/deliverability/domains/${domain.domain_id}`)
                      }
                    }}
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                        <Shield className="h-5 w-5 text-violet-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold truncate">
                            {domain.domain_name}
                          </p>
                          <StatusBadge status={reputationStatus} />
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {domain.sent > 0 ? (
                            <>
                              <span className="text-xs text-muted-foreground">
                                {domain.sent} sent
                              </span>
                              <span className="text-xs text-emerald-400">
                                {domain.delivered} delivered ({dRate}%)
                              </span>
                              {domain.bounced > 0 && (
                                <span className="text-xs text-red-400">
                                  {domain.bounced} bounced ({bRate}%)
                                </span>
                              )}
                              {domain.spam > 0 && (
                                <span className="text-xs text-amber-400">
                                  {domain.spam} spam ({sRate}%)
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              No sending data in the last 7 days
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-muted-foreground">Reputation</p>
                        <p
                          className={`text-sm font-semibold ${
                            domain.reputation_state === 'insufficient_data'
                              ? 'text-muted-foreground'
                              : domain.reputation_score >= 80
                                ? 'text-emerald-400'
                                : domain.reputation_score >= 60
                                  ? 'text-amber-400'
                                  : 'text-red-400'
                          }`}
                        >
                          {reputationLabel}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <EmptyState
              title="No domain data"
              description="Deliverability stats will appear once you start sending emails."
              icon={<Mail className="h-5 w-5" />}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard({
  icon,
  label,
  value,
  subValue,
  positive,
  negative,
}: {
  icon: React.ReactNode
  label: string
  value: number
  subValue?: string
  positive?: boolean
  negative?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">
            {icon}
          </div>
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{value.toLocaleString()}</span>
          {subValue && (
            <span
              className={`text-xs font-medium ${
                positive
                  ? 'text-emerald-400'
                  : negative
                    ? 'text-red-400'
                    : 'text-muted-foreground'
              }`}
            >
              {subValue}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
